import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowUpRight } from "lucide-react";
import { useLanguage, waLink, TOWNS } from "../i18n";

const EASE = [0.22, 1, 0.36, 1];
const COAST =
  "https://images.pexels.com/photos/34975869/pexels-photo-34975869.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1000";

const ServiceArea = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="zonas" ref={ref} className="overflow-hidden py-24 sm:py-32" data-testid="service-area-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-[#C85A32]"
          >
            {t.zones.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.08, ease: EASE }}
            className="font-serif text-4xl font-black tracking-tight text-[#0D1B2A] sm:text-5xl"
          >
            {t.zones.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.16, ease: EASE }}
            className="mt-5 max-w-lg text-base leading-relaxed text-[#0D1B2A]/60"
          >
            {t.zones.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.24, ease: EASE }}
            className="mt-8 flex flex-wrap gap-2.5"
          >
            {TOWNS.map((town) => (
              <a
                key={town.id}
                data-testid={`town-tag-${town.id}`}
                href={waLink(t.zones.waTown(town.name))}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1.5 rounded-full border border-[#0D1B2A]/15 bg-white px-4 py-2 text-sm font-semibold text-[#0D1B2A] transition-all duration-300 hover:border-[#1B6B93] hover:bg-[#1B6B93] hover:text-white"
              >
                <MapPin size={14} className="text-[#C85A32] transition-colors duration-300 group-hover:text-white" />
                {town.name}
              </a>
            ))}
          </motion.div>

          <motion.a
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.32, ease: EASE }}
            data-testid="zones-whatsapp-cta"
            href={waLink(t.waGeneric)}
            target="_blank"
            rel="noopener noreferrer"
            className="group mt-9 inline-flex items-center gap-2 text-base font-bold text-[#1B6B93] transition-colors duration-300 hover:text-[#C85A32]"
          >
            {t.zones.cta}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </motion.a>
        </div>

        <motion.div style={{ y }} className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE }}
            className="overflow-hidden rounded-[2.5rem] border-[10px] border-white shadow-2xl shadow-[#0D1B2A]/20"
          >
            <img
              src={COAST}
              alt="Pueblo mediterráneo de la Marina Baixa"
              className="h-[380px] w-full object-cover sm:h-[460px]"
              data-testid="zones-image"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, rotate: 6 }}
            whileInView={{ opacity: 1, rotate: 3 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="absolute -bottom-6 right-6 rounded-2xl bg-white px-6 py-4 font-script text-2xl text-[#1B6B93] shadow-xl shadow-[#0D1B2A]/15"
          >
            {t.zones.note}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceArea;
